import React, { useContext, useMemo, useState } from "react";
import { FilterMatchMode } from "primereact/api";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { Column } from "iuvity-ui-components/dist/column";
import { TableHeader } from "../datatable/TableHeader";
import { PickListModal } from "../shared/PickListModal";
import { FormSkeleton } from "../skeleton/FormSkeleton";
import { NewDialogProps } from "../dialog/NewCustomDialog";
import { useProductFields } from "../../hooks/useProductFields";
import { PRODUCT_STATUS_OPTIONS } from "../../constants";
import { AppContext } from "../../context/index";
import { InputText } from "iuvity-ui-components";
import { DomHandler } from "primereact/utils";
import { ProductFieldData } from "../../interfaces";

interface ProdDataTableActionsProps {
  usageType: string;
  serviceApi: string;
  dashboardOption?: boolean;
  isEditable?: boolean;
  limit?: number;
  auxModalController: React.Dispatch<React.SetStateAction<NewDialogProps>>;
}

export const ProdDataTableActions: React.FC<ProdDataTableActionsProps> = ({
  usageType,
  serviceApi,
  dashboardOption = false,
  isEditable = true,
  limit = 0,
  auxModalController,
}) => {
  const { t, toast } = useContext(AppContext);

  const {
    mappedFields,
    productFields,
    productFieldAction,
    filteredData,
    selectedFieldsIds,
    reloadFn,
  } = useProductFields(usageType, serviceApi, dashboardOption);
  const [valid, setValid] = useState<boolean>(false);

  const columns = useMemo(() => {
    const statusColumn = isEditable
      ? [
        {
          field: "status",
          header: t("Products.statusLabel"),
        }
      ]
      : [];
    return [
      {
        field: "name",
        header: t("Products.nameLabel"),
      },
      {
        field: "labelkey",
        header: t("Products.labelkeyLabel"),
      },
      {
        field: "order",
        header: t("Products.orderLabel"),
      },
      ...statusColumn,
    ];
  }, [t, isEditable]);

  const [visible, setVisible] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [editingRows, setEditingRows] = useState({});
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const onRowEditChange = (e: any) => {
    setEditingRows(e.data);
  };

  const onRowEditComplete = async (e: any) => {
    let { newData } = e;
    const updateStatus = await productFieldAction("FIELD", "UPDATE", [
      {
        id: newData.productfieldid,
        status: newData.status,
        order: +newData.order
      },
    ]);
    if (productFields && updateStatus === "SUCCESS") {
      reloadFn();
    }
  };

  const handleModalProps = (editorEvent: any) => {
    const { newData: { order,productfieldid} } = editorEvent;
    const orderAlreadyExist = filteredData?.find((item:ProductFieldData)=> item.productfieldid != productfieldid && item.order == order );
    
    if (isPositiveInteger(order) && !orderAlreadyExist) {
      auxModalController((prev) => ({
        ...prev,
        visible: true,
        title: t("Products.saveModalTitle"),
        content: t("Products.saveModalContent"),
        onConfirm: () => onRowEditComplete(editorEvent),
      }));
    } else {
      toast.current.replace({
        severity: "error",
        summary: "",
        detail: t('Products.validationErrorMessage'),
        life: 3000,
      });
    }
  };
  const isPositiveInteger = (val: any) => {
    let str = String(val);
    str = str.trim();
    if (!str) {
      return false;
    }
    str = str.replace(/^0+/, "") || "0";
    let n = Math.floor(Number(str));
    return n !== Infinity && String(n) === str && n > 0 && n <= 99;
  }

  const orderEditor = (options: any) => {
    return (
      // @ts-ignore
      <InputText
      label=""
      value={options.value}
      keyfilter={/^[0-9]*$/}
      onChange={(e) => options.editorCallback(e.target.value)}/>
    );
  };
  const statusEditor = (options: any) => {
    return (
      // @ts-ignore
      <Dropdown
        label=""
        value={options.value}
        onChange={(e) => options.editorCallback(e.target.value)}
        options={PRODUCT_STATUS_OPTIONS}
        filter={false}
        showClear={false}
        style={{
          border: "1px solid darkgray",
          borderRadius: "0.5rem",
          background: "white",
          padding: "0.5rem",
          maxWidth: "10rem",
        }}
      />
    );
  };

  return (
    <>
      <SubheaderActions
        className="pt-3 px-0 mx-3"
        actions={[
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("Products.addOrRemoveLabel"),
            onClick: () => {
              DomHandler.find(document.body, ".p-row-editor-cancel").forEach(
                (value, index) => {
                  setTimeout(() => {
                    value.click();
                  }, index * 5);
                }
              );
              setVisible(true)

            },
          },
        ]}
      />

      {filteredData ? (
        <DataTable
          className="m-3"
          header={
            <TableHeader
              title={t("Products.selectedFieldsLabel")}
              columns={columns}
              selectedColumns={selectedColumns}
              toggleColumns={setSelectedColumns}
              filters={filters}
              setFilters={setFilters}
              reload={reloadFn}
            />
          }
          currentPageReportTemplate={t(
            "ConsoleTranslations.rowsPaginatorTemplateLabel"
          )}
          paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
          sortField="order"
          sortOrder={1}
          editMode="row"
          dataKey="mappedfieldid"
          editingRows={editingRows}
          onRowEditChange={onRowEditChange}
          onRowEditComplete={handleModalProps}
          emptyMessage={t("TableActions.emptyMessage")}
          rows={10}
          size="normal"
          filters={filters}
          value={filteredData}
          scrollable
          resizableColumns
        >
          {selectedColumns.map((col, idx) => {
            return (
              <Column
                key={`${col.field}-${idx}`}
                field={col.field}
                header={col.header}
                sortable={!(col.field === "actions")}
                editor={
                  col.field === "status" && ((options) => statusEditor(options)) ||
                  col.field === "order" && ((options) => orderEditor(options))
                }
                align="left"
              />
            );
          })}
          {isEditable && (
            <Column
              key="actions"
              header={t("Products.actionsLabel")}
              align="center"
              rowEditor
              headerStyle={{ justifyContent: "center" }}

              style={{
                boxShadow: "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px",
                whiteSpace: "normal",
              }}
              frozen
              alignFrozen="right"
            />
          )}
        </DataTable>
      ) : (
        <BoxFull className="mx-2 p-0" style={{ background: "transparent" }}>
          <FormSkeleton length={3} />
        </BoxFull>
      )}

      {mappedFields && productFields && (
        <PickListModal
          sourceData={mappedFields
            .filter((field: any) => !selectedFieldsIds.includes(field.id))
            .map((field: any) => ({
              id: field.mappedfieldid,
              label: field.name,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label))}
          targetData={productFields
            .map((field: any) => ({
              id: field.mappedfieldid,
              label: field.name,
              order: field.order
            }))
            .sort((a: any, b: any) => a.order - b.order)}
          visible={visible}
          setVisible={setVisible}
          context={usageType}
          actionFn={productFieldAction}
          reloadFn={reloadFn}
          auxModalController={auxModalController}
          limit={limit}
        />
      )}
    </>
  );
};
